import type { RefObject } from 'preact';
import { useState, useRef } from 'preact/hooks';
import { Coords } from '../UNSAFE_Floating';
import { Size } from '../utils/UNSAFE_size';
import { Position, Rect } from '../utils/UNSAFE_flexibleComponentUtils';

type Dimensions = {
  width: number;
  height: number;
};

type DimensionsCss = {
  width: Size;
  height: Size;
};

export type DragMoveDetail = {
  originalPosition: Position;
  position: Position;
};

export type ResizeDetail = {
  originalPosition: Position;
  originalSize: Dimensions;
  position: Position;
  size: Dimensions;
};

type UseFlexibleComponentProps = {
  isDisabled?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
  onDragStart?: (detail: DragMoveDetail) => void;
  onDragMove?: (detail: DragMoveDetail) => void;
  onDragEnd?: (detail: DragMoveDetail) => void;
  onResizeStart?: (detail: ResizeDetail) => void;
  onResize?: (detail: ResizeDetail) => void;
  onResizeEnd?: (detail: ResizeDetail) => void;
};

/**
 * A custom hook that adds drag and/or resize capabilities to a component (e. g. Dialog).
 * The hook maintains the original and current position and size and returns handlers and
 * props to be spread on the component to make it "flexible". In addition, allows its consumer
 * to be notified when the drag or resize life-cycle events occur.
 */
export const useFlexibleComponent = ({
  isDisabled = false,
  isDraggable = false,
  isResizable = false,
  onDragStart: onDragStartHandler,
  onDragMove: onDragMoveHandler,
  onDragEnd: onDragEndHandler,
  onResizeStart: onResizeStartHandler,
  onResize: onResizeHandler,
  onResizeEnd: onResizeEndHandler
}: UseFlexibleComponentProps): {
  onDragStart?: (pos: Position) => void;
  onDragMove?: (pos: Position) => void;
  onDragEnd?: (pos: Position) => void;
  onResizeStart?: (rect: Rect) => void;
  onResize?: (rect: Rect) => void;
  onResizeEnd?: (rect: Rect) => void;
  anchorRef?: RefObject<Coords>;
  width?: Size;
  height?: Size;
} => {
  const [coords, _setCoords] = useState<Coords>();
  const [dimensions, setDimensions] = useState<DimensionsCss>();

  const coordsRef = useRef<Coords | null>(null);

  const origPosRef = useRef<Position | null>(null);
  const origSizeRef = useRef<Dimensions | null>(null);

  const setCoords = (coords: Coords) => {
    _setCoords({ x: coords.x, y: coords.y });
    coordsRef.current = { x: coords.x, y: coords.y };
  };

  if (isDisabled || (!isDraggable && !isResizable)) {
    return {};
  }

  const onDragStart = (pos: Position) => {
    setCoords({ x: pos.x, y: pos.y });
    origPosRef.current = { x: pos.x, y: pos.y };
    onDragStartHandler?.({
      originalPosition: { x: origPosRef.current.x, y: origPosRef.current.y },
      position: { x: pos.x, y: pos.y }
    });
  };

  const onDragMove = (pos: Position) => {
    setCoords({ x: pos.x, y: pos.y });
    onDragMoveHandler?.({
      originalPosition: { x: origPosRef.current!.x, y: origPosRef.current!.y },
      position: { x: pos.x, y: pos.y }
    });
  };

  const onDragEnd = (pos: Position) => {
    setCoords({ x: pos.x, y: pos.y });
    onDragEndHandler?.({
      originalPosition: { x: origPosRef.current!.x, y: origPosRef.current!.y },
      position: { x: pos.x, y: pos.y }
    });
    origPosRef.current = null;
  };

  const onResizeStart = (rect: Rect) => {
    setCoords({ x: rect.x, y: rect.y });
    origPosRef.current = { x: rect.x, y: rect.y };
    setDimensions({ width: (rect.width + 'px') as Size, height: (rect.height + 'px') as Size });
    origSizeRef.current = { width: rect.width, height: rect.height };
    onResizeStartHandler?.({
      originalPosition: { x: origPosRef.current!.x, y: origPosRef.current!.y },
      originalSize: { width: origSizeRef.current!.width, height: origSizeRef.current!.height },
      position: { x: rect.x, y: rect.y },
      size: { width: rect.width, height: rect.height }
    });
  };

  const onResize = (rect: Rect) => {
    setCoords({ x: rect.x, y: rect.y });
    setDimensions({ width: (rect.width + 'px') as Size, height: (rect.height + 'px') as Size });
    onResizeHandler?.({
      originalPosition: { x: origPosRef.current!.x, y: origPosRef.current!.y },
      originalSize: { width: origSizeRef.current!.width, height: origSizeRef.current!.height },
      position: { x: rect.x, y: rect.y },
      size: { width: rect.width, height: rect.height }
    });
  };

  const onResizeEnd = (rect: Rect) => {
    setCoords({ x: rect.x, y: rect.y });
    setDimensions({ width: (rect.width + 'px') as Size, height: (rect.height + 'px') as Size });
    onResizeEndHandler?.({
      originalPosition: { x: origPosRef.current!.x, y: origPosRef.current!.y },
      originalSize: { width: origSizeRef.current!.width, height: origSizeRef.current!.height },
      position: { x: rect.x, y: rect.y },
      size: { width: rect.width, height: rect.height }
    });
    origPosRef.current = null;
    origSizeRef.current = null;
  };

  return {
    ...(isDraggable && { onDragStart, onDragMove, onDragEnd }),
    ...(isResizable && { onResizeStart, onResize, onResizeEnd }),
    ...(coords && { anchorRef: coordsRef }),
    ...(dimensions && { width: dimensions.width, height: dimensions.height })
  };
};
